import { useEffect, useMemo, useState, type ReactNode } from "react";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { usersViewCopy } from "../app/i18n";
import { AnimatedModal, UserAvatarBadge } from "../app/ui";
import type { AppLanguage } from "../app/language";
import type { AuditEvent, RoleCapability, RolePreset, TicketCategory, UserTeam, WorkspaceMember } from "../app/types";
import { getWorkspaceMemberEffectiveCategoryIds } from "../app/utils";
import { canManageWorkspaceAccess, getEditableWorkspaceRoleCapabilityDefinitions, getRoleCapabilityLabel, getWorkspaceRoleLabel, isAgentRole, isSystemWorkspaceRole } from "../roles";

type MemberDraft = {
  name: string;
  email: string;
  role: string;
  password: string;
  resetPasswordOnLogin: boolean;
  allowedCategoryIds: string[] | null;
  teamIds: string[];
};

type TeamDraft = {
  name: string;
  description: string;
  categoryIds: string[];
};

type RolePresetDraft = {
  role: string;
  detail: string;
  capabilities: RoleCapability[];
};

type DirectoryModalView = "members" | "teams" | "roles";

type UsersViewProps = {
  language: AppLanguage;
  currentUserEmail: string | null;
  currentUserRole: string | null;
  billingPlan?: string | null;
  canEditUsers?: boolean;
  isLoadingUsers?: boolean;
  ticketCategories: TicketCategory[];
  teamMembers: WorkspaceMember[];
  teams: UserTeam[];
  onLoadRolePresets: () => Promise<RolePreset[]>;
  onCreateRolePreset: (input: RolePresetDraft) => Promise<void>;
  onUpdateRolePreset: (role: string, input: RolePresetDraft) => Promise<void>;
  onDeleteRolePreset: (role: string) => Promise<void>;
  onCreateUser: (input: MemberDraft) => Promise<void>;
  onUpdateUser: (email: string, input: { name: string; role: string; allowedCategoryIds: string[] | null; teamIds: string[]; status?: "enabled" | "disabled" }) => Promise<void>;
  onSetUserPassword: (email: string, newPassword: string, resetPasswordOnLogin?: boolean) => Promise<void>;
  onDeleteUser: (email: string) => Promise<void>;
  onCreateTeam: (input: TeamDraft) => Promise<void>;
  onUpdateTeam: (teamId: string, input: TeamDraft) => Promise<void>;
  onDeleteTeam: (teamId: string) => Promise<void>;
};

const emptyMemberDraft: MemberDraft = { name: "", email: "", role: "", password: "", resetPasswordOnLogin: false, allowedCategoryIds: null, teamIds: [] };
const emptyTeamDraft: TeamDraft = { name: "", description: "", categoryIds: [] };
const emptyRolePresetDraft: RolePresetDraft = { role: "", detail: "", capabilities: [] };
const modalCancelButtonClassName = "rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]";
const modalPrimaryButtonClassName = "brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50";

function UsersCard({
  eyebrow,
  title,
  description,
  titleActions,
  titleActionsBelow = false,
  actions,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  titleActions?: ReactNode;
  titleActionsBelow?: boolean;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
          {titleActionsBelow ? (
            <>
              <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
              {titleActions ? <div className="mt-3">{titleActions}</div> : null}
            </>
          ) : (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
              {titleActions ?? null}
            </div>
          )}
          {description ? <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p> : null}
        </div>
        {typeof actions === "string" ? <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{actions}</span> : actions ?? null}
      </div>
      {children}
    </section>
  );
}

function MetricCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function CountSpinner() {
  return (
    <span className="mt-3 inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{label}</span>
      {children}
    </label>
  );
}

function SectionSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
}) {
  return (
    <label className="block w-full sm:w-[24rem] lg:w-[28rem]">
      <span className="sr-only">{ariaLabel}</span>
      <div className="flex items-center gap-2 rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2.5 shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition focus-within:border-[var(--brand-primary)] focus-within:ring-4 focus-within:ring-[var(--brand-primary-ring)]">
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 shrink-0 text-[#98a2b3]" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.75 15a6.25 6.25 0 1 0 0-12.5 6.25 6.25 0 0 0 0 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m13.167 13.167 4.166 4.166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          type="search"
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full bg-transparent text-sm text-[#101828] outline-none placeholder:text-[#98a2b3]"
        />
      </div>
    </label>
  );
}

function ModalShell({
  open,
  dialogTitleId,
  eyebrow,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  dialogTitleId: string;
  eyebrow: string;
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      backdropAriaLabel="Close dialog"
      dialogLabelledBy={dialogTitleId}
      overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
      dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-4xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
    >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
            <h2 id={dialogTitleId} className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
              {title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <div className="mt-6 overflow-y-auto pr-1 pb-2">
          {children}
        </div>
    </AnimatedModal>
  );
}

function inputClassName(className?: string) {
  return `w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary-ring)] ${className ?? ""}`;
}

function getRolePermissionProfile(preset: RolePreset) {
  return {
    summary: preset.detail,
    permissions: preset.capabilities.length > 0 ? preset.capabilities.map(getRoleCapabilityLabel) : ["No permissions assigned"],
  };
}

function getRelativeDateInput(daysOffset: number) {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().slice(0, 10);
}

function formatAuditActionLabel(action: string) {
  return action
    .split(/[._]/g)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function formatAuditTargetTypeLabel(targetType: string) {
  return targetType
    .split(/[_-]/g)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function formatAuditTimestamp(value: string) {
  const timestamp = Date.parse(value);

  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function UsersView({
  language,
  currentUserEmail,
  currentUserRole,
  billingPlan = null,
  canEditUsers = false,
  isLoadingUsers = false,
  ticketCategories,
  teamMembers,
  teams,
  onLoadRolePresets,
  onCreateRolePreset,
  onUpdateRolePreset,
  onDeleteRolePreset,
  onCreateUser,
  onUpdateUser,
  onSetUserPassword,
  onDeleteUser,
  onCreateTeam,
  onUpdateTeam,
  onDeleteTeam,
}: UsersViewProps) {
  const copy = usersViewCopy[language];
  const [memberDraft, setMemberDraft] = useState<MemberDraft>(emptyMemberDraft);
  const [editingMemberEmail, setEditingMemberEmail] = useState<string | null>(null);
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [memberStatusDraft, setMemberStatusDraft] = useState<"enabled" | "disabled">("enabled");
  const [passwordMember, setPasswordMember] = useState<WorkspaceMember | null>(null);
  const [isPasswordFormOpen, setIsPasswordFormOpen] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordResetOnLogin, setPasswordResetOnLogin] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [teamDraft, setTeamDraft] = useState<TeamDraft>(emptyTeamDraft);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [isTeamFormOpen, setIsTeamFormOpen] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [rolePresetDraft, setRolePresetDraft] = useState<RolePresetDraft>(emptyRolePresetDraft);
  const [editingRolePresetName, setEditingRolePresetName] = useState<string | null>(null);
  const [isRolePresetFormOpen, setIsRolePresetFormOpen] = useState(false);
  const [rolePresetBusy, setRolePresetBusy] = useState(false);
  const [rolePresetError, setRolePresetError] = useState<string | null>(null);
  const [activeDirectoryModal, setActiveDirectoryModal] = useState<DirectoryModalView | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [rolePermissionSearch, setRolePermissionSearch] = useState("");
  const [rolePresets, setRolePresets] = useState<RolePreset[]>([]);
  const [rolePresetsBusy, setRolePresetsBusy] = useState(false);
  const [rolePresetsError, setRolePresetsError] = useState<string | null>(null);

  const effectiveCurrentRole = useMemo(
    () => currentUserRole ?? teamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.role ?? null,
    [currentUserEmail, currentUserRole, teamMembers],
  );
  const isSoloPlan = billingPlan?.trim().toLowerCase() === "solo";
  const canManage = useMemo(() => canManageWorkspaceAccess(effectiveCurrentRole), [effectiveCurrentRole]);
  const canEditWorkspaceUsers = canManage || canEditUsers;
  const activeMembers = teamMembers.filter(member => member.status === "Active").length;
  const admins = teamMembers.filter(member => canManageWorkspaceAccess(member.role)).length;
  const pendingInvites = teamMembers.filter(member => member.status === "Pending").length;
  const roleOptions = rolePresets.map(preset => preset.role);
  const editableCapabilities = getEditableWorkspaceRoleCapabilityDefinitions();
  const localizeRoleLabel = (value: string | null | undefined) => {
    const label = getWorkspaceRoleLabel(value);
    return copy.roleLabels[label as keyof typeof copy.roleLabels] ?? label;
  };
  const localizeRoleDetail = (value: string) => copy.roleDetails[value as keyof typeof copy.roleDetails] ?? value;
  const localizeCapabilityLabel = (capability: RoleCapability) => {
    const label = getRoleCapabilityLabel(capability);
    return copy.capabilityLabels[label as keyof typeof copy.capabilityLabels] ?? label;
  };
  const localizeCapabilityDetail = (value: string) => copy.capabilityDetails[value as keyof typeof copy.capabilityDetails] ?? value;
  const filteredEditableCapabilities = useMemo(() => {
    const normalizedSearch = rolePermissionSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return editableCapabilities;
    }

    return editableCapabilities.filter(definition => {
      const searchableValues = [
        definition.capability,
        definition.label,
        definition.detail,
        localizeCapabilityLabel(definition.capability),
        localizeCapabilityDetail(definition.detail),
      ];

      return searchableValues.some(value => value.toLowerCase().includes(normalizedSearch));
    });
  }, [copy, editableCapabilities, rolePermissionSearch]);
  const categoryNameById = useMemo(() => new Map(ticketCategories.map(category => [category.id, category.name])), [ticketCategories]);
  const teamNameById = useMemo(() => new Map(teams.map(team => [team.id, team.name])), [teams]);
  const teamMemberCountByTeamId = useMemo(() => {
    const counts = new Map<string, number>();

    for (const member of teamMembers) {
      for (const teamId of member.teamIds) {
        counts.set(teamId, (counts.get(teamId) ?? 0) + 1);
      }
    }

    return counts;
  }, [teamMembers]);
  const teamMemberDirectoryByTeamId = useMemo(() => {
    const directory = new Map<string, string[]>();

    for (const member of teamMembers) {
      for (const teamId of member.teamIds) {
        const entries = directory.get(teamId) ?? [];
        entries.push(member.name, member.email);
        directory.set(teamId, entries);
      }
    }

    return directory;
  }, [teamMembers]);
  const roleSeatLabelByRole = useMemo(() => {
    const counts = new Map<string, number>();

    for (const member of teamMembers) {
      const normalizedRole = member.role.trim().toLowerCase();
      counts.set(normalizedRole, (counts.get(normalizedRole) ?? 0) + 1);
    }

    return counts;
  }, [teamMembers]);
  const allAgentCategoriesSelected = useMemo(
    () => ticketCategories.length > 0 && ticketCategories.every(category => memberDraft.allowedCategoryIds?.includes(category.id) ?? false),
    [memberDraft.allowedCategoryIds, ticketCategories],
  );
  const teamScopedCategoryIds = useMemo(() => {
    const categoryIds = new Set<string>();

    for (const teamId of memberDraft.teamIds) {
      const team = teams.find(entry => entry.id === teamId);

      if (!team) {
        continue;
      }

      for (const categoryId of team.categoryIds) {
        categoryIds.add(categoryId);
      }
    }

    return [...categoryIds];
  }, [memberDraft.teamIds, teams]);
  const filteredTeamMembers = useMemo(() => {
    const normalizedSearch = memberSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return teamMembers;
    }

    return teamMembers.filter(member => {
      const searchableValues = [
        member.name,
        member.email,
        member.status,
        member.role,
        getWorkspaceRoleLabel(member.role),
        ...(member.teamIds ?? []).map(teamId => teamNameById.get(teamId) ?? "Archived team"),
        ...(member.allowedCategoryIds ?? []).map(categoryId => categoryNameById.get(categoryId) ?? "Archived category"),
      ];

      return searchableValues.some(value => value.toLowerCase().includes(normalizedSearch));
    });
  }, [categoryNameById, memberSearch, teamMembers, teamNameById]);
  const filteredRolePresets = useMemo(() => {
    const normalizedSearch = roleSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return rolePresets;
    }

    return rolePresets.filter(preset => {
      const searchableValues = [
        preset.role,
        getWorkspaceRoleLabel(preset.role),
        preset.detail,
        ...preset.capabilities.map(getRoleCapabilityLabel),
      ];

      return searchableValues.some(value => value.toLowerCase().includes(normalizedSearch));
    });
  }, [rolePresets, roleSearch]);
  const filteredTeams = useMemo(() => {
    const normalizedSearch = teamSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return teams;
    }

    return teams.filter(team => {
      const searchableValues = [
        team.name,
        team.description,
        ...team.categoryIds.map(categoryId => categoryNameById.get(categoryId) ?? "Archived category"),
        ...(teamMemberDirectoryByTeamId.get(team.id) ?? []),
      ];

      return searchableValues.some(value => value.toLowerCase().includes(normalizedSearch));
    });
  }, [categoryNameById, teamMemberDirectoryByTeamId, teamSearch, teams]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setRolePresetsBusy(true);
      setRolePresetsError(null);

      try {
        const nextRolePresets = await onLoadRolePresets();

        if (!cancelled) {
          setRolePresets(nextRolePresets);
        }
      } catch (error) {
        if (!cancelled) {
          setRolePresetsError(error instanceof Error ? error.message : copy.errors.loadRoles);
        }
      } finally {
        if (!cancelled) {
          setRolePresetsBusy(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [onLoadRolePresets]);

  const getAllCategoryIds = () => ticketCategories.map(category => category.id);

  const applyDraftRole = (role: string, allowedCategoryIds?: string[] | null) => {
    setMemberDraft(current => ({
      ...current,
      role,
      allowedCategoryIds: isAgentRole(role)
        ? allowedCategoryIds ?? current.allowedCategoryIds ?? getAllCategoryIds()
        : null,
    }));
  };

  const handleCloseMemberModal = () => {
    setIsMemberFormOpen(false);
    setEditingMemberEmail(null);
    setMemberError(null);
    setMemberStatusDraft("enabled");
    setMemberDraft(emptyMemberDraft);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordFormOpen(false);
    setPasswordMember(null);
    setPasswordDraft("");
    setPasswordConfirmation("");
    setPasswordResetOnLogin(false);
    setPasswordError(null);
  };

  const handleCloseTeamModal = () => {
    setIsTeamFormOpen(false);
    setEditingTeamId(null);
    setTeamError(null);
    setTeamDraft(emptyTeamDraft);
  };

  const handleCloseRolePresetModal = () => {
    setIsRolePresetFormOpen(false);
    setEditingRolePresetName(null);
    setRolePresetError(null);
    setRolePresetDraft(emptyRolePresetDraft);
    setRolePermissionSearch("");
  };

  const handleCloseDirectoryModal = () => {
    setActiveDirectoryModal(null);
  };

  const handleOpenMemberCreate = () => {
    setActiveDirectoryModal(null);
    setEditingMemberEmail(null);
    setMemberStatusDraft("enabled");
    setMemberDraft({ ...emptyMemberDraft, role: roleOptions[0] ?? "", allowedCategoryIds: isAgentRole(roleOptions[0] ?? "") ? getAllCategoryIds() : null, teamIds: [] });
    setMemberError(null);
    setIsMemberFormOpen(true);
  };

  const handleOpenMemberEdit = (member: WorkspaceMember) => {
    setActiveDirectoryModal(null);
    setEditingMemberEmail(member.email);
    setMemberStatusDraft(member.status === "Disabled" ? "disabled" : "enabled");
    setMemberDraft({
      name: member.name,
      email: member.email,
      role: member.role,
      password: "",
      resetPasswordOnLogin: false,
      allowedCategoryIds: isAgentRole(member.role) ? member.allowedCategoryIds ?? getAllCategoryIds() : null,
      teamIds: member.teamIds ?? [],
    });
    setMemberError(null);
    setIsMemberFormOpen(true);
  };

  const handleOpenPasswordModal = (member: WorkspaceMember) => {
    setActiveDirectoryModal(null);
    setPasswordMember(member);
    setPasswordDraft("");
    setPasswordConfirmation("");
    setPasswordResetOnLogin(false);
    setPasswordError(null);
    setIsPasswordFormOpen(true);
  };

  const handleOpenTeamCreate = () => {
    setActiveDirectoryModal(null);
    setEditingTeamId(null);
    setTeamDraft(emptyTeamDraft);
    setTeamError(null);
    setIsTeamFormOpen(true);
  };

  const handleOpenTeamEdit = (team: UserTeam) => {
    setActiveDirectoryModal(null);
    setEditingTeamId(team.id);
    setTeamDraft({
      name: team.name,
      description: team.description,
      categoryIds: [...team.categoryIds],
    });
    setTeamError(null);
    setIsTeamFormOpen(true);
  };

  const handleToggleMemberTeam = (teamId: string) => {
    setMemberDraft(current => ({
      ...current,
      teamIds: current.teamIds.includes(teamId)
        ? current.teamIds.filter(currentTeamId => currentTeamId !== teamId)
        : [...current.teamIds, teamId],
    }));
  };

  const handleToggleMemberCategory = (categoryId: string) => {
    setMemberDraft(current => {
      const nextAllowedCategoryIds = current.allowedCategoryIds ?? [];

      return {
        ...current,
        allowedCategoryIds: nextAllowedCategoryIds.includes(categoryId)
          ? nextAllowedCategoryIds.filter(currentCategoryId => currentCategoryId !== categoryId)
          : [...nextAllowedCategoryIds, categoryId],
      };
    });
  };

  const handleSaveMember = async () => {
    if (!memberDraft.name.trim() || !memberDraft.email.trim() || !memberDraft.role.trim()) {
      setMemberError(copy.errors.requiredMemberFields);
      return;
    }

    if (isAgentRole(memberDraft.role) && getWorkspaceMemberEffectiveCategoryIds({
      role: memberDraft.role,
      allowedCategoryIds: memberDraft.allowedCategoryIds ?? [],
      teamIds: memberDraft.teamIds,
    }, teams)?.length === 0) {
      setMemberError(copy.errors.assignAgentScope);
      return;
    }

    if (!editingMemberEmail && memberDraft.password.trim().length < 8) {
      setMemberError(copy.errors.temporaryPasswordTooShort);
      return;
    }

    setMemberBusy(true);
    setMemberError(null);

    try {
      if (editingMemberEmail) {
        await onUpdateUser(editingMemberEmail, {
          name: memberDraft.name.trim(),
          role: memberDraft.role.trim(),
          allowedCategoryIds: isAgentRole(memberDraft.role) ? memberDraft.allowedCategoryIds ?? [] : null,
          teamIds: memberDraft.teamIds,
          status: memberStatusDraft,
        });
      } else {
        await onCreateUser({
          name: memberDraft.name.trim(),
          email: memberDraft.email.trim(),
          role: memberDraft.role.trim(),
          password: memberDraft.password,
          resetPasswordOnLogin: memberDraft.resetPasswordOnLogin,
          allowedCategoryIds: isAgentRole(memberDraft.role) ? memberDraft.allowedCategoryIds ?? [] : null,
          teamIds: memberDraft.teamIds,
        });
      }

      setIsMemberFormOpen(false);
      setEditingMemberEmail(null);
      setMemberDraft(emptyMemberDraft);
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : copy.errors.saveUser);
    } finally {
      setMemberBusy(false);
    }
  };

  const handleToggleTeamCategory = (categoryId: string) => {
    setTeamDraft(current => ({
      ...current,
      categoryIds: current.categoryIds.includes(categoryId)
        ? current.categoryIds.filter(currentCategoryId => currentCategoryId !== categoryId)
        : [...current.categoryIds, categoryId],
    }));
  };

  const handleSaveTeam = async () => {
    if (!teamDraft.name.trim()) {
      setTeamError(copy.errors.teamNameRequired);
      return;
    }

    if (teamDraft.categoryIds.length === 0) {
      setTeamError(copy.errors.teamCategoryRequired);
      return;
    }

    setTeamBusy(true);
    setTeamError(null);

    try {
      if (editingTeamId) {
        await onUpdateTeam(editingTeamId, {
          name: teamDraft.name.trim(),
          description: teamDraft.description.trim(),
          categoryIds: teamDraft.categoryIds,
        });
      } else {
        await onCreateTeam({
          name: teamDraft.name.trim(),
          description: teamDraft.description.trim(),
          categoryIds: teamDraft.categoryIds,
        });
      }

      handleCloseTeamModal();
    } catch (error) {
      setTeamError(error instanceof Error ? error.message : copy.errors.saveTeam);
    } finally {
      setTeamBusy(false);
    }
  };

  const handleDeleteTeam = async (team: UserTeam) => {
    if (!window.confirm(copy.confirmations.deleteTeam(team.name))) {
      return;
    }

    setTeamBusy(true);
    setTeamError(null);

    try {
      await onDeleteTeam(team.id);
    } catch (error) {
      setTeamError(error instanceof Error ? error.message : copy.errors.deleteTeam);
    } finally {
      setTeamBusy(false);
    }
  };

  const handleRemoveMember = async (member: WorkspaceMember) => {
    if (!window.confirm(copy.confirmations.removeMember(member.name))) {
      return;
    }

    setMemberBusy(true);
    setMemberError(null);

    try {
      await onDeleteUser(member.email);
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : copy.errors.removeUser);
    } finally {
      setMemberBusy(false);
    }
  };

  const handleToggleMemberStatus = async (member: WorkspaceMember) => {
    setMemberBusy(true);
    setMemberError(null);

    try {
      await onUpdateUser(member.email, {
        name: member.name,
        role: member.role,
        allowedCategoryIds: isAgentRole(member.role) ? member.allowedCategoryIds ?? [] : null,
        teamIds: member.teamIds,
        status: member.status === "Disabled" ? "enabled" : "disabled",
      });
    } catch (error) {
      setMemberError(error instanceof Error ? error.message : copy.errors.saveUser);
    } finally {
      setMemberBusy(false);
    }
  };

  const handleSavePassword = async () => {
    if (!passwordMember) {
      return;
    }

    if (passwordDraft.length < 8) {
      setPasswordError(copy.errors.newPasswordTooShort);
      return;
    }

    if (passwordDraft !== passwordConfirmation) {
      setPasswordError(copy.errors.passwordMismatch);
      return;
    }

    setPasswordBusy(true);
    setPasswordError(null);

    try {
      await onSetUserPassword(passwordMember.email, passwordDraft, passwordResetOnLogin);
      handleClosePasswordModal();
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : copy.errors.updateLocalPassword);
    } finally {
      setPasswordBusy(false);
    }
  };

  const handleOpenRolePresetCreate = () => {
    setActiveDirectoryModal(null);
    setEditingRolePresetName(null);
    setRolePresetDraft(emptyRolePresetDraft);
    setRolePermissionSearch("");
    setRolePresetError(null);
    setIsRolePresetFormOpen(true);
  };

  const handleOpenRolePresetEdit = (preset: RolePreset) => {
    setActiveDirectoryModal(null);
    setEditingRolePresetName(preset.role);
    setRolePresetDraft({
      role: preset.role,
      detail: preset.detail,
      capabilities: [...preset.capabilities],
    });
    setRolePermissionSearch("");
    setRolePresetError(null);
    setIsRolePresetFormOpen(true);
  };

  useEffect(() => {
    if ((!isMemberFormOpen && !isPasswordFormOpen && !isTeamFormOpen && !isRolePresetFormOpen && !activeDirectoryModal) || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (isRolePresetFormOpen) {
        handleCloseRolePresetModal();
        return;
      }

      if (isTeamFormOpen) {
        handleCloseTeamModal();
        return;
      }

      if (isPasswordFormOpen) {
        handleClosePasswordModal();
        return;
      }

      if (activeDirectoryModal) {
        handleCloseDirectoryModal();
        return;
      }

      handleCloseMemberModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeDirectoryModal, isMemberFormOpen, isPasswordFormOpen, isRolePresetFormOpen, isTeamFormOpen]);

  const handleToggleRoleCapability = (capability: RoleCapability) => {
    setRolePresetDraft(current => ({
      ...current,
      capabilities: current.capabilities.includes(capability)
        ? current.capabilities.filter(currentCapability => currentCapability !== capability)
        : [...current.capabilities, capability],
    }));
  };

  const handleSaveRolePreset = async () => {
    if (!rolePresetDraft.role.trim() || !rolePresetDraft.detail.trim()) {
      setRolePresetError(copy.errors.requiredRoleFields);
      return;
    }

    setRolePresetBusy(true);
    setRolePresetError(null);

    try {
      if (editingRolePresetName) {
        await onUpdateRolePreset(editingRolePresetName, {
          role: rolePresetDraft.role.trim(),
          detail: rolePresetDraft.detail.trim(),
          capabilities: rolePresetDraft.capabilities,
        });
      } else {
        await onCreateRolePreset({
          role: rolePresetDraft.role.trim(),
          detail: rolePresetDraft.detail.trim(),
          capabilities: rolePresetDraft.capabilities,
        });
      }

      setRolePresets(await onLoadRolePresets());

      setIsRolePresetFormOpen(false);
      setEditingRolePresetName(null);
      setRolePresetDraft(emptyRolePresetDraft);
    } catch (error) {
      setRolePresetError(error instanceof Error ? error.message : copy.errors.saveRolePreset);
    } finally {
      setRolePresetBusy(false);
    }
  };

  const handleDeleteRole = async (preset: RolePreset) => {
    if (!window.confirm(copy.confirmations.deleteRole(preset.role))) {
      return;
    }

    setRolePresetBusy(true);
    setRolePresetError(null);

    try {
      await onDeleteRolePreset(preset.role);
      setRolePresets(await onLoadRolePresets());
    } catch (error) {
      setRolePresetError(error instanceof Error ? error.message : copy.errors.deleteRolePreset);
    } finally {
      setRolePresetBusy(false);
    }
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.page.description}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label={copy.metrics.activeUsersLabel} value={String(activeMembers).padStart(2, "0")} detail={copy.metrics.activeUsersDetail} isLoading={isLoadingUsers} />
        <MetricCard label={copy.metrics.adminsLabel} value={String(admins).padStart(2, "0")} detail={copy.metrics.adminsDetail} isLoading={isLoadingUsers} />
        <MetricCard label={copy.metrics.pendingInvitesLabel} value={String(pendingInvites).padStart(2, "0")} detail={copy.metrics.pendingInvitesDetail} isLoading={isLoadingUsers} />
        <MetricCard label={copy.metrics.accessPolicyLabel} value={canEditWorkspaceUsers ? copy.metrics.managedValue : copy.metrics.readOnlyValue} detail={canEditWorkspaceUsers ? copy.metrics.managedDetail : copy.metrics.readOnlyDetail} isLoading={isLoadingUsers} />
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <UsersCard
          eyebrow={copy.cards.users.eyebrow}
          title={copy.cards.users.title}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                onClick={() => setActiveDirectoryModal("members")}
                aria-haspopup="dialog"
                aria-expanded={activeDirectoryModal === "members"}
              >
                {copy.actions.view}
              </button>
              {canEditWorkspaceUsers ? (
                <button className="brand-primary-button rounded-xl px-4 py-2 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50" disabled={!canEditWorkspaceUsers || isSoloPlan} onClick={handleOpenMemberCreate}>
                  {copy.actions.addWorkspaceUser}
                </button>
              ) : (
                <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.cards.users.readOnlyBadge}</span>
              )}
            </div>
          }
        >
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.users.summaryTitle}</p>
              {isLoadingUsers ? <CountSpinner /> : <p className="mt-3 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{teamMembers.length}</p>}
              <p className="mt-1 text-sm text-[#667085]">{copy.cards.users.summaryDescription}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {teamMembers.slice(0, 4).map(member => (
                  <span key={member.email} className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#344054] ring-1 ring-[#e4e7ec]">
                    {member.name}
                  </span>
                ))}
                {teamMembers.length > 4 ? (
                  <span className="rounded-full bg-[#f3f4f6] px-3 py-1.5 text-[11px] font-semibold text-[#475467]">{copy.counts.more(teamMembers.length - 4)}</span>
                ) : null}
              </div>
            </div>

            <div className="rounded-[24px] border border-[#eaecf0] bg-[#f8fafc] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.users.includesTitle}</p>
              <ul className="mt-3 space-y-2 text-sm text-[#475467]">
                {copy.cards.users.includesItems.map(item => <li key={item}>• {item}</li>)}
              </ul>
            </div>
          </div>
        </UsersCard>

        <UsersCard
          eyebrow={copy.cards.teams.eyebrow}
          title={copy.cards.teams.title}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                onClick={() => setActiveDirectoryModal("teams")}
                aria-haspopup="dialog"
                aria-expanded={activeDirectoryModal === "teams"}
              >
                {copy.actions.view}
              </button>
              {canManage ? (
                <button className="brand-primary-button rounded-xl px-4 py-2 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50" disabled={!canManage} onClick={handleOpenTeamCreate}>
                  {copy.actions.addTeam}
                </button>
              ) : (
                <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.teamsOnly(teams.length)}</span>
              )}
            </div>
          }
        >
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.teams.summaryTitle}</p>
              {isLoadingUsers ? <CountSpinner /> : <p className="mt-3 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{teams.length}</p>}
              <p className="mt-1 text-sm text-[#667085]">{copy.cards.teams.summaryDescription}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {teams.slice(0, 4).map(team => (
                  <span key={team.id} className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#344054] ring-1 ring-[#e4e7ec]">
                    {team.name}
                  </span>
                ))}
                {teams.length > 4 ? (
                  <span className="rounded-full bg-[#f3f4f6] px-3 py-1.5 text-[11px] font-semibold text-[#475467]">{copy.counts.more(teams.length - 4)}</span>
                ) : null}
              </div>
            </div>

            <div className="rounded-[24px] border border-[#eaecf0] bg-[#f8fafc] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.teams.includesTitle}</p>
              <ul className="mt-3 space-y-2 text-sm text-[#475467]">
                {copy.cards.teams.includesItems.map(item => <li key={item}>• {item}</li>)}
              </ul>
            </div>
          </div>
        </UsersCard>

        <UsersCard
          eyebrow={copy.cards.roles.eyebrow}
          title={copy.cards.roles.title}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                onClick={() => setActiveDirectoryModal("roles")}
                aria-haspopup="dialog"
                aria-expanded={activeDirectoryModal === "roles"}
              >
                {copy.actions.view}
              </button>
              {canManage ? (
                <button className="brand-primary-button rounded-xl px-4 py-2 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50" disabled={!canManage} onClick={handleOpenRolePresetCreate}>
                  {copy.actions.addCustomRole}
                </button>
              ) : (
                <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.cards.roles.readOnlyBadge}</span>
              )}
            </div>
          }
        >
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.roles.summaryTitle}</p>
              {isLoadingUsers || rolePresetsBusy ? <CountSpinner /> : <p className="mt-3 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{rolePresets.length}</p>}
              <p className="mt-1 text-sm text-[#667085]">{copy.cards.roles.summaryDescription}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {rolePresets.slice(0, 4).map(preset => (
                  <span key={preset.role} className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#344054] ring-1 ring-[#e4e7ec]">
                    {localizeRoleLabel(preset.role)}
                  </span>
                ))}
                {rolePresets.length > 4 ? (
                  <span className="rounded-full bg-[#f3f4f6] px-3 py-1.5 text-[11px] font-semibold text-[#475467]">{copy.counts.more(rolePresets.length - 4)}</span>
                ) : null}
              </div>
              {rolePresetsError ? <p className="mt-3 text-sm font-medium text-[#b42318]">{rolePresetsError}</p> : null}
            </div>

            <div className="rounded-[24px] border border-[#eaecf0] bg-[#f8fafc] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.cards.roles.includesTitle}</p>
              <ul className="mt-3 space-y-2 text-sm text-[#475467]">
                {copy.cards.roles.includesItems.map(item => <li key={item}>• {item}</li>)}
              </ul>
            </div>
          </div>
        </UsersCard>
      </div>

      <ModalShell
        open={activeDirectoryModal === "members"}
        dialogTitleId="workspace-members-directory-dialog-title"
        eyebrow={copy.modals.members.eyebrow}
        title={copy.modals.members.title}
        description={copy.modals.members.description}
        onClose={handleCloseDirectoryModal}
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionSearchInput value={memberSearch} onChange={setMemberSearch} placeholder={copy.modals.members.searchPlaceholder} ariaLabel={copy.modals.members.searchAriaLabel} />
            <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.usersSummary(filteredTeamMembers.length, teamMembers.length)}</span>
          </div>

          <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
            {filteredTeamMembers.length > 0 ? filteredTeamMembers.map(member => (
              <div key={member.email} className="flex flex-wrap items-center justify-between gap-4 rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                <div className="flex items-center gap-4">
                  <UserAvatarBadge
                    avatarUrl={member.avatarUrl ?? null}
                    initials={member.initials}
                    className="brand-primary-avatar flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-bold"
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-[#101828]">{member.name}</p>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${member.status === "Active" ? "bg-[#effaf5] text-[#027a48]" : member.status === "Disabled" ? "bg-[#fef2f2] text-[#b42318]" : "bg-[#fff7ed] text-[#c4320a]"}`}>
                        {copy.statusLabels[member.status as keyof typeof copy.statusLabels] ?? member.status}
                      </span>
                      {currentUserEmail === member.email ? <span className="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#027a48]">{copy.list.you}</span> : null}
                    </div>
                    <p className="mt-1 text-sm text-[#667085]">{localizeRoleLabel(member.role)} · {member.email}</p>
                    {(member.teamIds ?? []).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {member.teamIds.map(teamId => (
                          <span key={teamId} className="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#027a48] ring-1 ring-[#d1fadf]">
                            {teamNameById.get(teamId) ?? copy.list.archivedTeam}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {isAgentRole(member.role) ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(member.allowedCategoryIds ?? []).length > 0 ? (
                          member.allowedCategoryIds!.map(categoryId => (
                            <span key={categoryId} className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#475467] ring-1 ring-[#e4e7ec]">
                              {categoryNameById.get(categoryId) ?? copy.list.archivedCategory}
                            </span>
                          ))
                        ) : (
                          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#475467] ring-1 ring-[#e4e7ec]">
                            {copy.list.allCategories}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!canEditWorkspaceUsers || currentUserEmail === member.email || memberBusy || (isSoloPlan && member.status === "Disabled")}
                    onClick={() => void handleToggleMemberStatus(member)}
                  >
                    {member.status === "Disabled" ? copy.actions.enableUser : copy.actions.disableUser}
                  </button>
                  <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50" disabled={!canEditWorkspaceUsers || passwordBusy} onClick={() => handleOpenPasswordModal(member)}>
                    {copy.actions.setPassword}
                  </button>
                  <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50" disabled={!canEditWorkspaceUsers} onClick={() => handleOpenMemberEdit(member)}>
                    {copy.actions.edit}
                  </button>
                  <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#fecaca] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-50" disabled={!canEditWorkspaceUsers || currentUserEmail === member.email || memberBusy} onClick={() => void handleRemoveMember(member)}>
                    {copy.actions.remove}
                  </button>
                </div>
              </div>
            )) : (
              <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-sm font-semibold text-[#101828]">{copy.modals.members.emptyTitle}</p>
                <p className="mt-1 text-sm text-[#667085]">{copy.modals.members.emptyDescription}</p>
              </div>
            )}
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={activeDirectoryModal === "teams"}
        dialogTitleId="workspace-teams-directory-dialog-title"
        eyebrow={copy.modals.teams.eyebrow}
        title={copy.modals.teams.title}
        description={copy.modals.teams.description}
        onClose={handleCloseDirectoryModal}
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionSearchInput value={teamSearch} onChange={setTeamSearch} placeholder={copy.modals.teams.searchPlaceholder} ariaLabel={copy.modals.teams.searchAriaLabel} />
            <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.teamsSummary(filteredTeams.length, teams.length)}</span>
          </div>

          <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
            {filteredTeams.length > 0 ? filteredTeams.map(team => (
              <div key={team.id} className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-[#101828]">{team.name}</p>
                      <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.members(teamMemberCountByTeamId.get(team.id) ?? 0)}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#667085]">{team.description || copy.list.noTeamDescription}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {team.categoryIds.map(categoryId => (
                        <span key={categoryId} className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#344054] ring-1 ring-[#e4e7ec]">
                          {categoryNameById.get(categoryId) ?? copy.list.archivedCategory}
                        </span>
                      ))}
                    </div>
                  </div>
                  {canManage ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50" disabled={teamBusy} onClick={() => handleOpenTeamEdit(team)}>
                        {copy.actions.edit}
                      </button>
                      <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#fecaca] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-50" disabled={teamBusy} onClick={() => void handleDeleteTeam(team)}>
                        {copy.actions.delete}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            )) : (
              <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-sm font-semibold text-[#101828]">{teams.length > 0 ? copy.modals.teams.emptySearchTitle : copy.modals.teams.emptyDefaultTitle}</p>
                <p className="mt-1 text-sm text-[#667085]">{teams.length > 0 ? copy.modals.teams.emptySearchDescription : copy.modals.teams.emptyDefaultDescription}</p>
              </div>
            )}
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={activeDirectoryModal === "roles"}
        dialogTitleId="workspace-roles-directory-dialog-title"
        eyebrow={copy.modals.roles.eyebrow}
        title={copy.modals.roles.title}
        description={copy.modals.roles.description}
        onClose={handleCloseDirectoryModal}
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionSearchInput value={roleSearch} onChange={setRoleSearch} placeholder={copy.modals.roles.searchPlaceholder} ariaLabel={copy.modals.roles.searchAriaLabel} />
            <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.rolesSummary(filteredRolePresets.length, rolePresets.length)}</span>
          </div>

          {rolePresetsError ? <p className="text-sm font-medium text-[#b42318]">{rolePresetsError}</p> : null}
          {rolePresetsBusy ? <p className="text-sm text-[#667085]">{copy.list.loadingRoles}</p> : null}

          <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
            {filteredRolePresets.length > 0 ? filteredRolePresets.map(preset => (
              <div key={preset.role} className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                {(() => {
                  const permissionProfile = getRolePermissionProfile(preset);
                  const isSystemRole = isSystemWorkspaceRole(preset.role);

                  return (
                    <>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-[#101828]">{localizeRoleLabel(preset.role)}</p>
                          <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${isSystemRole ? "bg-[#f3f4f6] text-[#475467]" : "bg-[#ecfdf3] text-[#027a48]"}`}>{isSystemRole ? copy.list.system : copy.list.custom}</span>
                          <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.counts.seats(roleSeatLabelByRole.get(preset.role.trim().toLowerCase()) ?? 0)}</span>
                        </div>
                        {canManage && !isSystemRole ? (
                          <div className="flex flex-wrap items-center gap-3">
                            <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50" disabled={rolePresetBusy} onClick={() => handleOpenRolePresetEdit(preset)}>
                              {copy.actions.edit}
                            </button>
                            <button className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#fecaca] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-50" disabled={rolePresetBusy} onClick={() => void handleDeleteRole(preset)}>
                              {copy.actions.delete}
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <p className="mt-2 text-sm text-[#667085]">{localizeRoleDetail(permissionProfile.summary)}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {permissionProfile.permissions.map(permission => (
                          <span
                            key={permission}
                            className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[#344054] ring-1 ring-[#e4e7ec]"
                          >
                            {permission === "No permissions assigned" ? copy.list.noPermissionsAssigned : copy.capabilityLabels[permission as keyof typeof copy.capabilityLabels] ?? permission}
                          </span>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )) : (
              <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-sm font-semibold text-[#101828]">{copy.modals.roles.emptyTitle}</p>
                <p className="mt-1 text-sm text-[#667085]">{copy.modals.roles.emptyDescription}</p>
              </div>
            )}
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={isPasswordFormOpen}
        dialogTitleId="workspace-user-password-dialog-title"
        eyebrow={copy.modals.password.eyebrow}
        title={copy.modals.password.title(passwordMember?.name)}
        description={copy.modals.password.description}
        onClose={handleClosePasswordModal}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label={copy.modals.password.emailAddress}>
            <input className={inputClassName("bg-[#f8fafc] text-[#667085]")} disabled value={passwordMember?.email ?? ""} />
          </FormField>
          <div className="hidden md:block" />
          <FormField label={copy.modals.password.newPassword}>
            <input
              className={inputClassName()}
              type="password"
              autoComplete="new-password"
              value={passwordDraft}
              onChange={event => setPasswordDraft(event.target.value)}
              placeholder={copy.modals.password.newPasswordPlaceholder}
            />
          </FormField>
          <FormField label={copy.modals.password.confirmPassword}>
            <input
              className={inputClassName()}
              type="password"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={event => setPasswordConfirmation(event.target.value)}
              placeholder={copy.modals.password.confirmPasswordPlaceholder}
            />
          </FormField>
        </div>

        <label className="mt-4 flex items-start gap-3 rounded-[22px] border border-[#d0d5dd] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
          <input
            type="checkbox"
            checked={passwordResetOnLogin}
            onChange={event => setPasswordResetOnLogin(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-[#d0d5dd] text-[var(--brand-primary)] focus:ring-[var(--brand-primary-ring)]"
          />
          <span>
            <span className="block text-sm font-semibold text-[#101828]">{copy.modals.password.resetPasswordOnLogin}</span>
            <span className="mt-1 block text-sm leading-6 text-[#667085]">{copy.modals.password.resetPasswordOnLoginDescription}</span>
          </span>
        </label>

        {passwordError ? <p className="mt-4 text-sm font-semibold text-[#b42318]">{passwordError}</p> : null}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button type="button" className={modalCancelButtonClassName} onClick={handleClosePasswordModal} disabled={passwordBusy}>
            {copy.actions.cancel}
          </button>
          <button type="button" className={modalPrimaryButtonClassName} onClick={() => void handleSavePassword()} disabled={passwordBusy}>
            {passwordBusy ? copy.actions.saving : copy.actions.savePassword}
          </button>
        </div>
      </ModalShell>

      <ModalShell
          open={isMemberFormOpen}
          dialogTitleId="workspace-user-dialog-title"
          eyebrow={editingMemberEmail ? copy.modals.memberForm.editEyebrow : copy.modals.memberForm.createEyebrow}
          title={editingMemberEmail ? memberDraft.name || copy.modals.memberForm.fallbackTitle : copy.modals.memberForm.createTitle}
          description={editingMemberEmail ? copy.modals.memberForm.editDescription : copy.modals.memberForm.createDescription}
          onClose={handleCloseMemberModal}
        >
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <FormField label={copy.modals.memberForm.fullName}>
              <input className={inputClassName()} value={memberDraft.name} onChange={event => setMemberDraft(current => ({ ...current, name: event.target.value }))} placeholder={copy.modals.memberForm.fullNamePlaceholder} />
            </FormField>
            <FormField label={copy.modals.memberForm.emailAddress}>
              <input className={inputClassName(editingMemberEmail ? "bg-[#f8fafc] text-[#667085]" : undefined)} disabled={Boolean(editingMemberEmail)} type="email" value={memberDraft.email} onChange={event => setMemberDraft(current => ({ ...current, email: event.target.value }))} placeholder={copy.modals.memberForm.emailPlaceholder} />
            </FormField>
            <FormField label={copy.modals.memberForm.role}>
              <select className={inputClassName()} value={memberDraft.role} onChange={event => applyDraftRole(event.target.value)}>
                <option value="">{copy.modals.memberForm.selectRole}</option>
                {roleOptions.map(role => (
                  <option key={role} value={role}>{localizeRoleLabel(role)}</option>
                ))}
              </select>
            </FormField>
            {editingMemberEmail ? (
              <FormField label={copy.modals.memberForm.accountStatus}>
                <select
                  className={inputClassName()}
                  value={memberStatusDraft}
                  disabled={isSoloPlan && memberStatusDraft === "disabled"}
                  onChange={event => setMemberStatusDraft(event.target.value === "disabled" ? "disabled" : "enabled")}
                >
                  <option value="enabled">{copy.modals.memberForm.statusEnabled}</option>
                  <option value="disabled">{copy.modals.memberForm.statusDisabled}</option>
                </select>
              </FormField>
            ) : null}
            {!editingMemberEmail ? (
              <FormField label={copy.modals.memberForm.temporaryPassword}>
                <input className={inputClassName()} type="password" value={memberDraft.password} onChange={event => setMemberDraft(current => ({ ...current, password: event.target.value }))} placeholder={copy.modals.memberForm.temporaryPasswordPlaceholder} />
              </FormField>
            ) : null}
          </div>

          {!editingMemberEmail ? (
            <label className="mt-4 flex items-start gap-3 rounded-[22px] border border-[#d0d5dd] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
              <input
                type="checkbox"
                checked={memberDraft.resetPasswordOnLogin}
                onChange={event => setMemberDraft(current => ({ ...current, resetPasswordOnLogin: event.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-[#d0d5dd] text-[var(--brand-primary)] focus:ring-[var(--brand-primary-ring)]"
              />
              <span>
                <span className="block text-sm font-semibold text-[#101828]">{copy.modals.memberForm.resetPasswordOnLogin}</span>
                <span className="mt-1 block text-sm leading-6 text-[#667085]">{copy.modals.memberForm.resetPasswordOnLoginDescription}</span>
              </span>
            </label>
          ) : null}

          <div className="mt-4 rounded-[22px] border border-[#d0d5dd] bg-[#f8fafc] p-4">
            <div>
              <p className="text-sm font-semibold text-[#101828]">{copy.modals.memberForm.teamAssignmentsTitle}</p>
              <p className="mt-1 text-sm text-[#667085]">{copy.modals.memberForm.teamAssignmentsDescription}</p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {teams.length > 0 ? teams.map(team => {
                const selected = memberDraft.teamIds.includes(team.id);

                return (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => handleToggleMemberTeam(team.id)}
                    className={`rounded-full px-3 py-2 text-[11px] font-semibold transition-colors ${selected ? "bg-[#101828] text-white" : "bg-white text-[#475467] ring-1 ring-[#d0d5dd] hover:bg-[#f9fafb]"}`}
                  >
                    {team.name}
                  </button>
                );
              }) : <p className="text-sm text-[#667085]">{copy.modals.memberForm.noTeams}</p>}
            </div>
          </div>

          {isAgentRole(memberDraft.role) ? (
            <div className="mt-4 rounded-[22px] border border-[#d0d5dd] bg-[#f8fafc] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#101828]">{copy.modals.memberForm.directCategoryScopeTitle}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.modals.memberForm.directCategoryScopeDescription}</p>
                </div>
                <button
                  type="button"
                  className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#344054] ring-1 ring-[#d0d5dd]"
                  onClick={() => setMemberDraft(current => ({ ...current, allowedCategoryIds: allAgentCategoriesSelected ? [] : getAllCategoryIds() }))}
                >
                  {allAgentCategoriesSelected ? copy.modals.memberForm.deselectAll : copy.modals.memberForm.selectAll}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {ticketCategories.map(category => {
                  const selected = memberDraft.allowedCategoryIds?.includes(category.id) ?? false;

                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleToggleMemberCategory(category.id)}
                      className={`rounded-full px-3 py-2 text-[11px] font-semibold transition-colors ${selected ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[#475467] ring-1 ring-[#d0d5dd] hover:bg-[#f9fafb]"}`}
                    >
                      {category.name}
                    </button>
                  );
                })}
              </div>

              {teamScopedCategoryIds.length > 0 ? (
                <div className="mt-4 rounded-2xl bg-white/80 px-4 py-3 ring-1 ring-[#e4e7ec]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.modals.memberForm.inheritedFromTeams}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {teamScopedCategoryIds.map(categoryId => (
                      <span key={categoryId} className="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#027a48] ring-1 ring-[#d1fadf]">
                        {categoryNameById.get(categoryId) ?? copy.list.archivedCategory}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {memberError ? <p className="mt-4 text-sm font-medium text-[#b42318]">{memberError}</p> : null}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button className={modalCancelButtonClassName} onClick={handleCloseMemberModal}>
              {copy.actions.cancel}
            </button>
            <button className={modalPrimaryButtonClassName} disabled={memberBusy} onClick={() => void handleSaveMember()}>
              {memberBusy ? copy.actions.saving : editingMemberEmail ? copy.actions.saveChanges : copy.actions.createUser}
            </button>
          </div>
        </ModalShell>

      <ModalShell
          open={isTeamFormOpen}
          dialogTitleId="workspace-team-dialog-title"
          eyebrow={editingTeamId ? copy.modals.teamForm.editEyebrow : copy.modals.teamForm.createEyebrow}
          title={editingTeamId ? teamDraft.name || copy.modals.teamForm.fallbackTitle : copy.modals.teamForm.createTitle}
          description={copy.modals.teamForm.description}
          onClose={handleCloseTeamModal}
        >
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <FormField label={copy.modals.teamForm.teamName}>
              <input className={inputClassName()} value={teamDraft.name} onChange={event => setTeamDraft(current => ({ ...current, name: event.target.value }))} placeholder={copy.modals.teamForm.teamNamePlaceholder} />
            </FormField>
            <FormField label={copy.modals.teamForm.descriptionLabel}>
              <input className={inputClassName()} value={teamDraft.description} onChange={event => setTeamDraft(current => ({ ...current, description: event.target.value }))} placeholder={copy.modals.teamForm.descriptionPlaceholder} />
            </FormField>
          </div>

          <div className="mt-4 rounded-[22px] border border-[#d0d5dd] bg-[#f8fafc] p-4">
            <div>
              <p className="text-sm font-semibold text-[#101828]">{copy.modals.teamForm.categoryCoverageTitle}</p>
              <p className="mt-1 text-sm text-[#667085]">{copy.modals.teamForm.categoryCoverageDescription}</p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {ticketCategories.map(category => {
                const selected = teamDraft.categoryIds.includes(category.id);

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleToggleTeamCategory(category.id)}
                    className={`rounded-full px-3 py-2 text-[11px] font-semibold transition-colors ${selected ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[#475467] ring-1 ring-[#d0d5dd] hover:bg-[#f9fafb]"}`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          {teamError ? <p className="mt-4 text-sm font-medium text-[#b42318]">{teamError}</p> : null}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button className={modalCancelButtonClassName} onClick={handleCloseTeamModal}>
              {copy.actions.cancel}
            </button>
            <button className={modalPrimaryButtonClassName} disabled={teamBusy} onClick={() => void handleSaveTeam()}>
              {teamBusy ? copy.actions.saving : editingTeamId ? copy.actions.saveTeam : copy.actions.createTeam}
            </button>
          </div>
        </ModalShell>

      <ModalShell
          open={isRolePresetFormOpen}
          dialogTitleId="workspace-role-dialog-title"
          eyebrow={editingRolePresetName ? copy.modals.roleForm.editEyebrow : copy.modals.roleForm.createEyebrow}
          title={editingRolePresetName ? rolePresetDraft.role || copy.modals.roleForm.fallbackTitle : copy.modals.roleForm.createTitle}
          description={copy.modals.roleForm.description}
          onClose={handleCloseRolePresetModal}
        >
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <FormField label={copy.modals.roleForm.roleName}>
              <input className={inputClassName()} value={rolePresetDraft.role} onChange={event => setRolePresetDraft(current => ({ ...current, role: event.target.value }))} placeholder={copy.modals.roleForm.roleNamePlaceholder} />
            </FormField>
            <FormField label={copy.modals.roleForm.descriptionLabel}>
              <input className={inputClassName()} value={rolePresetDraft.detail} onChange={event => setRolePresetDraft(current => ({ ...current, detail: event.target.value }))} placeholder={copy.modals.roleForm.descriptionPlaceholder} />
            </FormField>
          </div>

          <div className="mt-4 rounded-[22px] border border-[#d0d5dd] bg-[#f8fafc] p-4">
            <div>
              <p className="text-sm font-semibold text-[#101828]">{copy.modals.roleForm.permissionsTitle}</p>
              <p className="mt-1 text-sm text-[#667085]">{copy.modals.roleForm.permissionsDescription}</p>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <SectionSearchInput
                value={rolePermissionSearch}
                onChange={setRolePermissionSearch}
                placeholder={copy.modals.roleForm.permissionsSearchPlaceholder}
                ariaLabel={copy.modals.roleForm.permissionsSearchAriaLabel}
              />
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
                {filteredEditableCapabilities.length}/{editableCapabilities.length}
              </span>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {filteredEditableCapabilities.length > 0 ? filteredEditableCapabilities.map(definition => {
                const selected = rolePresetDraft.capabilities.includes(definition.capability);

                return (
                  <button
                    key={definition.capability}
                    type="button"
                    onClick={() => handleToggleRoleCapability(definition.capability)}
                    className={`rounded-[20px] border px-4 py-4 text-left transition-colors ${selected ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]" : "border-[#d0d5dd] bg-white hover:bg-[#f9fafb]"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#101828]">{localizeCapabilityLabel(definition.capability)}</p>
                        <p className="mt-1 text-xs text-[#667085]">{localizeCapabilityDetail(definition.detail)}</p>
                      </div>
                      <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border text-[11px] ${selected ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white" : "border-[#d0d5dd] text-transparent"}`}>
                        ✓
                      </span>
                    </div>
                  </button>
                );
              }) : (
                <div className="rounded-[20px] border border-dashed border-[#d0d5dd] bg-white px-4 py-6 text-sm text-[#667085] md:col-span-2">
                  {copy.modals.roleForm.permissionsNoResults}
                </div>
              )}
            </div>
          </div>

          {rolePresetError ? <p className="mt-4 text-sm font-medium text-[#b42318]">{rolePresetError}</p> : null}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button className={modalCancelButtonClassName} onClick={handleCloseRolePresetModal}>
              {copy.actions.cancel}
            </button>
            <button className={modalPrimaryButtonClassName} disabled={rolePresetBusy} onClick={() => void handleSaveRolePreset()}>
              {rolePresetBusy ? copy.actions.saving : editingRolePresetName ? copy.actions.saveRole : copy.actions.createRole}
            </button>
          </div>
        </ModalShell>
    </section>
  );
}
